// Palworld TCG weekly market report — the citable, writer-friendly companion to
// /tcg-price-index. The index page publishes the benchmark and its methodology;
// this page turns the same board into a short, quotable weekly recap (level,
// direction, breadth, biggest risers and fallers) that creators and journalists
// can cite without reading the methodology first.
//
// No publish/update dates are rendered or emitted in schema (site-wide rule).
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { getPriceBoard } from "@/lib/prices.functions";
import { buildPriceRows, formatMoney, type PriceRow } from "@/lib/prices-shared";
import { buildIndexBoard, formatChange, formatLevel } from "@/lib/price-index.shared";
import { CardImage } from "@/components/CardImage";
import { RarityBadge } from "@/components/RarityBadge";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { InternalLinkBlock } from "@/components/InternalLinkBlock";
import { AffiliateDisclosure } from "@/components/AffiliateDisclosure";
import { breadcrumbSchema, toolSchema } from "@/lib/schema";
import { cardLinkProps } from "@/lib/card-link";
import { useReportTracking } from "@/lib/use-report-tracking";


const URL = "https://palstars.com/tcg-market-report";
const TITLE = "Palworld TCG Market Report: Card Price Shifts";
const DESCRIPTION =
  "Why did your Palworld card values change? Read today's comprehensive TCG market report tracking volatile price explosions and card value spikes.";

const FAQ: FaqItem[] = [
  {
    q: "Are Palworld TCG cards going up or down this week?",
    a: "Read the index level at the top of this report. Above 1,000 the average tracked Palworld card printing is worth more than it was at the start of the window, below 1,000 it is worth less, and the 7-day figure next to it is this week's move. The breadth line tells you whether the move is broad or carried by a handful of chase cards.",
  },
  {
    q: "Which Palworld cards rose the most this week?",
    a: "The risers table lists the biggest 7-day gainers among tracked printings, with the current market value in USD next to each one. Every card links to its own page with the full price history.",
  },
  {
    q: "Can I quote this Palworld market report?",
    a: "Yes, free of charge, with attribution and a link to palstars.com. There is also a public JSON feed at /api/public/price-index.json if you would rather pull the numbers into your own chart or video graphic.",
  },
  {
    q: "How often is the Palworld TCG market report rebuilt?",
    a: "The underlying board syncs daily at 07:10 UTC and this report is generated from the latest sync every time it loads, so the weekly figures always end on the most recent completed data.",
  },
];

export const Route = createFileRoute("/tcg-market-report")({
  // Server-side render from the real price board so first paint (crawler and
  // browser alike) matches the hydrated client render.
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: ["price-board"],
      queryFn: () => getPriceBoard(),
      staleTime: 5 * 60 * 1000,
    }),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Palworld TCG market report — weekly movers" },
      {
        property: "og:description",
        content:
          "The Pal Stars index level, market breadth and the biggest Palworld card risers and fallers of the week.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([
        ["Home", "/"],
        ["Price Index", "/tcg-price-index"],
        ["Market Report", "/tcg-market-report"],
      ]),
      toolSchema({ name: "Palworld TCG Market Report", description: DESCRIPTION, path: "/tcg-market-report" }),
      faqSchema(FAQ, URL),
    ],
  }),
  component: MarketReportPage,
});

function Delta({ value }: { value: number }) {
  const tone = value > 0 ? "text-emerald-300" : value < 0 ? "text-rose-300" : "text-white/50";
  const Icon = value > 0 ? ArrowUpRight : value < 0 ? ArrowDownRight : Minus;
  return (
    <span className={`inline-flex items-center gap-1 font-semibold ${tone}`}>
      <Icon className="h-4 w-4" aria-hidden="true" />
      {formatChange(value)}
    </span>
  );
}

function MoverList({ title, rows, onPick }: { title: string; rows: PriceRow[]; onPick: (code: string) => void }) {
  return (
    <section className="rounded-2xl border border-white/10 bg-white/5 p-4">
      <h2 className="text-sm font-bold">{title}</h2>
      <ul className="mt-2">
        {rows.map((row) => (
          <li key={row.code} className="flex items-center gap-3 border-t border-white/10 py-2 first:border-t-0">
            <Link {...cardLinkProps(row.code)} className="shrink-0" onClick={() => onPick(row.code)}>
              <CardImage
                src={row.image}
                alt={`${row.name} ${row.code} Palworld card`}
                width={40}
                height={56}
                className="h-14 w-10 rounded object-cover"
              />
            </Link>
            <div className="min-w-0 flex-1">
              <Link
                {...cardLinkProps(row.code)}
                className="block truncate text-sm font-semibold hover:underline"
                onClick={() => onPick(row.code)}
              >
                {row.name}
              </Link>
              <p className="flex items-center gap-2 text-[11px] text-white/55">
                <span className="font-mono">{row.code}</span>
                <RarityBadge code={row.code} />
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold tabular-nums">{formatMoney(row.market, "USD")}</p>
              <Delta value={row.change7d} />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function MarketReportPage() {
  const { data } = useQuery({
    queryKey: ["price-board"],
    queryFn: () => getPriceBoard(),
    staleTime: 10 * 60 * 1000,
  });

  const rows = useMemo(() => data?.rows ?? buildPriceRows([]), [data]);
  const board = useMemo(() => buildIndexBoard(rows), [rows]);
  const c = board.composite;
  const track = useReportTracking();

  const direction = !c ? "flat" : c.change7d > 0 ? "up" : c.change7d < 0 ? "down" : "flat";
  const answer = c
    ? `The Palworld TCG market is ${direction} this week: the Pal Stars TCG Price Index sits at ${formatLevel(
        c.level,
      )} (base 1,000), a ${formatChange(c.change7d)} move over seven days across ${c.constituents} tracked card printings, with ${c.advancing} rising, ${c.declining} falling and ${c.unchanged} unchanged.`
    : "Market data is loading.";

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">Palworld TCG Market Report</h1>
      <p className="aeo-answer mt-3 text-white/80">{answer}</p>

      {c && (
        <div className="mt-6 grid gap-3 sm:grid-cols-4">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <p className="text-xs text-white/50">Index level</p>
            <p className="text-2xl font-extrabold tabular-nums">{formatLevel(c.level)}</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <p className="text-xs text-white/50">7-day</p>
            <p className="text-2xl font-extrabold tabular-nums">
              <Delta value={c.change7d} />
            </p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <p className="text-xs text-white/50">30-day</p>
            <p className="text-2xl font-extrabold tabular-nums">
              <Delta value={c.change30d} />
            </p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <p className="text-xs text-white/50">Breadth (7d)</p>
            <p className="text-sm font-semibold">
              {c.advancing} up / {c.declining} down / {c.unchanged} flat
            </p>
          </div>
        </div>
      )}

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <MoverList title="Biggest risers this week" rows={board.leaders.slice(0, 8)} onPick={(code) => track("card_click", code)} />
        <MoverList title="Biggest fallers this week" rows={board.laggards.slice(0, 8)} onPick={(code) => track("card_click", code)} />
      </div>

      <section className="mt-8 rounded-2xl border border-white/10 bg-white/5 p-4">
        <h2 className="text-sm font-bold">Quote this report</h2>
        <p className="mt-2 text-sm text-white/70">
          Free to quote with attribution. Machine-readable feed:{" "}
          <a
            className="text-primary underline"
            href="/api/public/price-index.json"
            rel="nofollow"
            onClick={() => track("feed_open", "price-index.json")}
          >
            /api/public/price-index.json
          </a>
          . Methodology lives on the{" "}
          <Link to="/tcg-price-index" className="text-primary underline">
            Palworld TCG Price Index
          </Link>
          .
        </p>
        <p
          className="mt-2 rounded-lg bg-black/30 p-3 font-mono text-xs text-white/80"
          onCopy={() => track("citation_copy", "market-report")}
        >
          Pal Stars TCG Price Index: {c ? formatLevel(c.level) : "1,000"} (equal-weight, base 1,000) —
          https://palstars.com/tcg-price-index
        </p>
      </section>

      <InternalLinkBlock path="/tcg-price-index" />
      <FaqSection items={FAQ} />
      <AffiliateDisclosure />
    </div>
  );
}
